import { useEffect, useRef, useState, type RefObject } from 'react';
import type { VoiceConnection } from '@wundo/shared/roleplay.ts';
import {
  isMicrophoneBlocked,
  type VoiceAgentState,
  type VoiceFailure,
} from '@/lib/roleplayCall.ts';
import { RealtimeVoiceClient } from '@/lib/realtimeVoice.ts';
import { rememberMicrophoneId, rememberSpeakerId } from '@/lib/audioDevices.ts';
import { reportError } from '@/lib/reportError.ts';
import { PracticeRealtimeSettings } from './PracticeRealtimeSettings.tsx';
import { PracticeVoiceDock } from './PracticeVoiceDock.tsx';
import { PracticeVoiceStage } from './PracticeVoiceStage.tsx';

export interface PracticeRealtimeCallProps {
  connection: VoiceConnection;
  personaName: string;
  /** Handed the way to hang up, so the screen can go silent the instant End is pressed. */
  hangUpRef: RefObject<(() => Promise<void>) | null>;
  agentState: VoiceAgentState | null;
  captionsVisible: boolean;
  /** What was chosen on the card before the call started. */
  microphoneId: string | null;
  speakerId: string | null;
  /**
   * Whether the user is on their way out. A call that stops on its own is a
   * failure worth saying out loud; the same event on a call somebody just ended
   * is the end of the conversation.
   */
  leaving: boolean;
  onAgentState: (state: VoiceAgentState | null) => void;
  onSuggestEnd: () => void;
  /** The character hung up after its goodbye: end the call as the end button does. */
  onCallEnded: () => void;
  onToggleCaptions: () => void;
}

/**
 * A spoken practice conversation over one socket to our API. This screen holds
 * the microphone itself and the line it talks over is a plain WebSocket.
 */
export function PracticeRealtimeCall({
  connection,
  personaName,
  hangUpRef,
  agentState,
  captionsVisible,
  microphoneId,
  speakerId,
  leaving,
  onAgentState,
  onSuggestEnd,
  onCallEnded,
  onToggleCaptions,
}: PracticeRealtimeCallProps) {
  const [attempt, setAttempt] = useState(0);
  const [failure, setFailure] = useState<VoiceFailure | undefined>(undefined);
  const [level, setLevel] = useState(0);
  const [caption, setCaption] = useState('');
  /* One answer could not be spoken; cleared as soon as the character speaks again. */
  const [voiceTrouble, setVoiceTrouble] = useState(false);
  const [microphoneOn, setMicrophoneOn] = useState(true);
  const [microphone, setMicrophone] = useState(microphoneId);
  const [speaker, setSpeaker] = useState(speakerId);
  const client = useRef<RealtimeVoiceClient | null>(null);

  /*
   * Read inside the client's own callbacks, which are registered once when the
   * call is made — a plain prop read there would be the one from the render
   * the client was made in.
   */
  const latest = useRef({ onAgentState, onSuggestEnd, onCallEnded, leaving });
  useEffect(() => {
    latest.current = { onAgentState, onSuggestEnd, onCallEnded, leaving };
  });

  const { url } = connection;
  useEffect(() => {
    const voice = new RealtimeVoiceClient(
      { url, microphoneId, speakerId },
      {
        /* "Connecting" is the orb's own state, which it reads as no state at all. */
        onState: (state) => {
          if (state === 'speaking') {
            setVoiceTrouble(false);
          }
          latest.current.onAgentState(state === 'connecting' ? null : state);
        },
        onLevel: setLevel,
        /* Captions subtitle the character; the user hears themselves. */
        onCaption: setCaption,
        onSuggestEnd: () => latest.current.onSuggestEnd(),
        onEnded: () => latest.current.onCallEnded(),
        onVoiceTrouble: () => setVoiceTrouble(true),
        onError: (message) => {
          reportError(new Error(message), {
            component: 'PracticeRealtimeCall',
            action: 'bridge',
            url,
          });
        },
        onClosed: () => {
          latest.current.onAgentState(null);
          if (latest.current.leaving) {
            return;
          }

          /*
           * A call that stops on its own after it was working is its own
           * failure: without this the orb would go back to "connecting" and a
           * dropped conversation would look like one that never started.
           */
          setFailure((current) => current ?? 'dropped');
        },
      },
    );

    client.current = voice;
    hangUpRef.current = () => voice.disconnect();

    void voice.connect().catch((caught: unknown) => {
      /* A blocked microphone is the user's answer: no retry fixes it, no report is owed. */
      if (isMicrophoneBlocked(caught)) {
        setFailure('blocked');
        return;
      }

      setFailure('connect');
      reportError(caught, {
        component: 'PracticeRealtimeCall',
        action: 'connect',
        url,
        navigator_online: navigator.onLine,
      });
    });

    return () => {
      hangUpRef.current = null;
      client.current = null;
      void voice.disconnect();
    };
  }, [attempt, url, microphoneId, speakerId, hangUpRef]);

  function toggleMicrophone() {
    const next = !microphoneOn;
    setMicrophoneOn(next);
    client.current?.setMicrophoneEnabled(next);
  }

  function pickMicrophone(deviceId: string) {
    rememberMicrophoneId(deviceId);
    setMicrophone(deviceId);
    void client.current?.setMicrophone(deviceId).catch((caught: unknown) => {
      /* Unplugged between the list and the pick: the running microphone stays. */
      if (
        isMicrophoneBlocked(caught) ||
        (caught instanceof Error && caught.name === 'OverconstrainedError')
      ) {
        return;
      }
      reportError(caught, { component: 'PracticeRealtimeCall', action: 'microphone' });
    });
  }

  function pickSpeaker(deviceId: string) {
    rememberSpeakerId(deviceId);
    setSpeaker(deviceId);
    void client.current?.setSpeaker(deviceId);
  }

  function retry() {
    setFailure(undefined);
    setCaption('');
    setVoiceTrouble(false);
    setLevel(0);
    setMicrophoneOn(true);
    setAttempt((count) => count + 1);
  }

  if (failure !== undefined) {
    return (
      <PracticeVoiceStage
        personaName={personaName}
        agentState={null}
        failure={failure}
        captionsVisible={captionsVisible}
        /* A dropped call is over; the way on from here is the feedback. */
        onRetry={failure === 'dropped' ? undefined : retry}
      />
    );
  }

  return (
    <>
      <PracticeVoiceStage
        personaName={personaName}
        agentState={agentState}
        /*
         * The microphone is the only thing measured here, and while the
         * character is talking an echo-cancelled microphone hears nothing —
         * the orb animates the character's turn on its own instead.
         */
        level={agentState === null || agentState === 'speaking' ? undefined : level}
        caption={caption}
        captionsVisible={captionsVisible}
        voiceTrouble={voiceTrouble}
      />
      <PracticeVoiceDock
        microphoneOn={microphoneOn}
        captionsVisible={captionsVisible}
        audioSettings={
          <PracticeRealtimeSettings
            microphoneId={microphone}
            speakerId={speaker}
            onPickMicrophone={pickMicrophone}
            onPickSpeaker={pickSpeaker}
          />
        }
        onToggleMicrophone={toggleMicrophone}
        onToggleCaptions={onToggleCaptions}
      />
    </>
  );
}
